import {
  Box,
  CardHeader,
  Form,
  Heading,
  Layer,
  Select,
  Tab,
  Tabs,
  Text,
} from 'grommet';
import { Close } from 'grommet-icons';
import {
  useEffect,
  useRef,
  Suspense,
  useState,
  useCallback,
  useMemo,
} from 'react';

import SHDButton from '@components/SHD/Button';

import {
  FieldCollege,
  FieldCommitStatus,
  FieldGPA,
  FieldGradYear,
  FieldNameFirst,
  FieldNameHandle,
  FieldNameLast,
  FieldNameLocation,
} from '@helpers/FormFields';
import TagInput from '@helpers/TagInput';

import PlayerSettingsTab from './PlayerSettingsTab';
import PlayerIntegrationsTab from './PlayerIntegrationsTab';

import { SPORTS_MAP } from '@constants/strings';

import { BasicLoader } from '@helpers/Loaders';
import { PaywallPageContext } from '@components/Paywall/paywall.types';

import useEntitlements from '@hooks/monetization/useEntitlements';
import { usePaywallContext } from '@contexts/PaywallContext';

import useClaimedPlayer from './useClaimedPlayer';
import { ClaimedPlayer } from '../claimedPlayer';

import PlayerPositions from './PlayerPositions';
import PlayerSocials from './PlayerSocials';
import PlayerAvatar from './PlayerAvatar';
import PlayerFavorites from './PlayerFavorites';
import PlayerBiometrics from './PlayerBiometrics';
import SHDText from '@ui/Text/SHDText';
import SHDImage from '@ui/Image/SHDImage';
import SHDThemeOverride from '@ui/ThemeOverride/SHDThemeOverride';

const PlayerProfileSettings = ({
  claimedPlayerId = '',
  closeLayer = () => {},
}) => {
  const {
    fetchClaimedPlayer,
    updateClaimedPlayer,
    uploadPlayerImage,
    claimedPlayer,
    claimedPlayerTeams,
    loading,
    error,
  } = useClaimedPlayer(claimedPlayerId, closeLayer);

  const footerRef = useRef<HTMLDivElement | null>(null);
  const [value, setValue] = useState<ClaimedPlayer | undefined>();
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [teamOptions, setTeamOptions] = useState<
    { label: string; value: string; sportType: string }[] | undefined
  >(undefined);

  useEffect(() => {
    if (claimedPlayer) {
      setValue(claimedPlayer);
    }
  }, [claimedPlayer, claimedPlayerTeams]);

  useEffect(() => {
    fetchClaimedPlayer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getTeamOptions = () => {
      const teamsMap = Object.fromEntries(
        claimedPlayerTeams.map((team) => [team.id, team])
      );

      const options = value?.primaryTeamIds.map((teamId) => ({
        label: teamsMap[teamId].nameLong,
        value: teamId,
        sportType: teamsMap[teamId].sport,
      }));
      return options;
    };

    const tOptions = getTeamOptions();
    setTeamOptions(tOptions);
  }, [claimedPlayerTeams, value?.primaryTeamIds]);

  const playerSports = useMemo(() => {
    const sports = new Set(teamOptions?.map((t) => t.sportType));
    return sports;
  }, [teamOptions]);

  const isBBSB = useMemo(() => {
    return teamOptions?.some(
      (team) => team.sportType === 'Baseball' || team.sportType === 'Softball'
    );
  }, [teamOptions]);

  const {
    entitlementStatus,
    canEditMonetizedFields,
    loading: viewerEntitlementLoading,
  } = useEntitlements({
    playerId: claimedPlayer?.id || '',
    sport: claimedPlayer?.primarySport,
  });

  const { renderCTA, handlePaywallOpen, handlePaywallClose } =
    usePaywallContext({
      entitlementStatus: entitlementStatus,
      trackingContext: PaywallPageContext.PLAYER_SETTINGS,
    });

  const updateValues = useCallback((newValue: Partial<ClaimedPlayer>) => {
    setValue((preValue) => {
      if (!preValue) {
        return preValue;
      }
      return { ...preValue, ...newValue };
    });
  }, []);

  const onPhoneNumberChanged = (phoneList: string[]) => {
    if (value) {
      const newValue: ClaimedPlayer = { ...value };
      newValue.notifyGuestPhoneNumbers = phoneList;
      updateValues({ notifyGuestPhoneNumbers: phoneList });
    }
  };

  const saveEdit = useCallback(async () => {
    handlePaywallClose();
    await updateClaimedPlayer({ ...value } as ClaimedPlayer);
  }, [handlePaywallClose, updateClaimedPlayer, value]);

  const renderPlayerProfile = () => {
    const primaryTeam = value?.primaryTeamIds && value?.primaryTeamIds[0];

    return (
      <Box
        fill="horizontal"
        pad={{ horizontal: 'medium', top: 'medium', bottom: '108px' }}
        gap="medium"
        id="edit-player-box"
        overflow="auto"
        height="100%"
      >
        <Box style={{ flexShrink: 0 }}>
          {error && <Text color="status-critical">{error}</Text>}
          <PlayerAvatar
            updateValues={updateValues}
            uploadPlayerImage={uploadPlayerImage}
            defaultImage={claimedPlayer?.imageUrlLarge}
          />
          <FieldNameFirst />
          <FieldNameLast required={false} />
          <FieldNameHandle
            label="Handle"
            placeholder="mike-trout27"
            onChange={() => {}}
          />
        </Box>
        <Box pad="small" gap="small" style={{ flexShrink: 0 }}></Box>
        {teamOptions && (
          <>
            <Text>Primary team</Text>
            <Select
              placeholder="Select"
              labelKey="label"
              value={primaryTeam}
              valueKey={{ key: 'value', reduce: true }}
              options={teamOptions}
              onChange={({ option }) => {
                const newTeamIdPriority = [option.value].concat(
                  value?.primaryTeamIds.filter(
                    (teamId) => teamId !== option.value
                  )
                );
                updateValues({
                  primaryTeamIds: newTeamIdPriority,
                });
              }}
            >
              {(option, state) => (
                <Box
                  direction="row"
                  pad="small"
                  gap="xsmall"
                  background={state.active ? 'active' : undefined}
                  align="center"
                >
                  {SPORTS_MAP[option.sportType].icon}
                  {option.sportType}
                  {option.label}
                </Box>
              )}
            </Select>
          </>
        )}

        {!viewerEntitlementLoading ? (
          <Box
            style={{ flexShrink: 0 }}
            gap={'small'}
            onClick={() => {
              canEditMonetizedFields ? null : handlePaywallOpen();
            }}
          >
            <SHDThemeOverride full={false}>
              <Box
                pad={{ top: 'xxsmall' }}
                direction="row"
                gap="xsmall"
                align="center"
              >
                <SHDText style="subhead-semibold">
                  Additional numbers to notify
                </SHDText>
                <SHDImage
                  src="/paywall/pro_primary.svg"
                  width={49.9}
                  alt="edit_player_pro_logo"
                />
              </Box>
              <SHDText style="body" color="gray-700">
                Add up to 5 phone numbers to receive in-game and post-game
                highlight texts of your player.
              </SHDText>
            </SHDThemeOverride>
            <TagInput
              value={value?.notifyGuestPhoneNumbers}
              onTagAdded={onPhoneNumberChanged}
              onTagRemoved={onPhoneNumberChanged}
              limit={5}
            />
          </Box>
        ) : (
          <Box margin={{ top: '1.5rem' }} style={{ flexShrink: 0 }}>
            {viewerEntitlementLoading ? (
              <BasicLoader />
            ) : (
              renderCTA({
                mode: 'phone',
                context: PaywallPageContext.PLAYER_SETTINGS,
                playerId: claimedPlayerId,
              })
            )}
          </Box>
        )}
        <Box style={{ flexShrink: 0 }}>
          <Heading level={5}>Player info</Heading>
          <FieldNameLocation isTeam={false} />
          <FieldGradYear />
          <PlayerFavorites
            playerSports={playerSports}
            value={value}
            updateValues={updateValues}
          />
          <PlayerPositions
            playerSports={playerSports}
            updateValues={updateValues}
            positions={value?.positions}
          />
          <PlayerBiometrics values={value} updateValues={updateValues} />
          <FieldCommitStatus updateValues={updateValues} />
          {value?.collegeCommitStatus === 10 ? <FieldCollege /> : null}
          <FieldGPA />
        </Box>
        <Box style={{ flexShrink: 0 }}>
          <PlayerSocials values={value} updateValues={updateValues} />
        </Box>
      </Box>
    );
  };

  return (
    <Layer
      id="edit-player-outer-layer"
      modal
      full="vertical"
      style={{ height: '100dvh' }}
      animation={false}
      onClickOutside={handlePaywallClose}
    >
      <Form
        value={value}
        onChange={(nextValue) => {
          setValue(nextValue);
        }}
        onReset={() => {
          if (claimedPlayer) {
            setValue({ ...claimedPlayer });
          }
        }}
        onSubmit={saveEdit}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <CardHeader>
          <Box width="24px" />
          <Box justify="center" align="center">
            <Text alignSelf="center" size="large" weight="bold">
              Edit Player
            </Text>
          </Box>
          <SHDButton icon={<Close />} onClick={closeLayer} justify="end" />
        </CardHeader>
        <Box
          style={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            height: '100%',
          }}
        >
          <Suspense>
            <Tabs
              activeIndex={activeTabIndex}
              onActive={(nextIndex) => setActiveTabIndex(nextIndex)}
            >
              <Tab id="profile-tab" title="Profile">
                {renderPlayerProfile()}
              </Tab>
              <Tab id="settings-tab" title="Settings">
                <PlayerSettingsTab
                  closeLayer={() => {
                    (closeLayer(), handlePaywallClose());
                  }}
                  value={value}
                  updateValues={updateValues}
                  loading={loading}
                  error={error}
                  isBBSB={isBBSB || false}
                />
              </Tab>
              <Tab id="integrations-tab" title="Integrations">
                <PlayerIntegrationsTab
                  closeLayer={() => {
                    (closeLayer(), handlePaywallClose());
                  }}
                  claimedPlayer={claimedPlayer}
                />
              </Tab>
            </Tabs>
          </Suspense>
        </Box>
        {activeTabIndex < 2 && (
          <Box
            ref={(el) => {
              footerRef.current = el;
            }}
            fill="horizontal"
            style={{
              position: 'fixed',
              left: 0,
              bottom: 0,
              width: '100%',
              zIndex: 10,
            }}
            pad={{
              horizontal: 'small',
              vertical: '22px',
            }}
            background="light-3"
          >
            <SHDButton
              primary
              size="large"
              type="submit"
              label={loading ? '...Saving' : 'Save'}
              disabled={
                JSON.stringify({ ...claimedPlayer }) ===
                  JSON.stringify(value) || loading
              }
              color="tertiary-1"
            />
          </Box>
        )}
      </Form>
    </Layer>
  );
};

export default PlayerProfileSettings;
